import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, useIntl } from 'react-intl';
import isEqual from 'lodash-es/isEqual';
import moment from 'moment';
import momentPropTypes from 'react-moment-proptypes';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaCalendar } from 'react-icons/fa';

import intlShape from '@/shared/utils/intlShape';
import RegistrationComponent from '@/shared/components/registration/RegistrationComponent';
import OkCancelModal from '@/shared/components/OkCancelModal';
import api from '@/shared/api';
import config from '@/config';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import AuthService, { functionalities } from '@/shared/services/AuthService';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import classNames from 'classnames';
import messages from '../../../shared/components/registration/registrationMessages';
import { FormGroupPart, RelationFormGroup } from '../../../shared/components/registration/partials';
import ResourceAutoComplete from '../../../shared/components/registration/ResourceAutoComplete';
import { retrieveResources } from '../../../shared/components/registration/resourceApi';
import DataCacheService from '@/shared/services/DataCacheService';
import { getOwnCompanyRelations } from '@/shared/components/input/OwnCompanyRelationSelect';
import { getRelationName } from '@/shared/components/RelationName';
import { dynamicSort } from '@/shared/utils/dynamicSort';

class BatchFillGapsRegistrationModal extends Component {
  state = {
    resources: null,
    resource: null,
    relationId: null,
    dateFrom: moment.utc().startOf('day'),
    dateUntil: moment.utc().startOf('day'),
    showValidation: false,
    ownCompanyRelations: null,
  };

  constructor(props) {
    super(props);
    let { dateFrom, dateUntil } = props;

    if (
      dateUntil &&
      dateFrom &&
      moment.utc(dateUntil).diff(moment.utc(dateFrom), 'years', true) > 1
    ) {
      //Range bigger than 1 year
      dateFrom = dateUntil.clone().startOf('year');
    }

    this.state.dateFrom = dateFrom;
    this.state.dateUntil = dateUntil;
  }

  componentDidMount() {
    const { resourceType, intl } = this.props;
    getOwnCompanyRelations().then(({ data }) => {
      const companies = DataCacheService.getDataCache()?.company;
      const ownCompanyRelations = data
        .map(x => ({ ...x, label: getRelationName(intl, x) }))
        .sort(dynamicSort('label'));

      let relationId = null;
      if (companies.length === 1) {
        relationId = companies[0].absenceRelationId;
      }
      if (!relationId && ownCompanyRelations === 1) {
        relationId = ownCompanyRelations[0].id;
      }
      this.setState({
        ownCompanyRelations,
        relationId,
        hasMultipleCompanies: ownCompanyRelations > 1,
      });
    });
    retrieveResources(intl, resourceType).then(resources => {
      this.setState({
        resources,
        resource: resources.length === 1 ? resources[0] : null,
      });
    });
  }

  saveRegistration = () => {
    if (this.isFormInvalid()) return;
    const { intl, onSave } = this.props;
    const { dateFrom, dateUntil, isRecup, resource, relation } = this.state;
    const data = {
      dateFrom,
      dateUntil,
      resourceId: resource.id,
      relationId: relation?.id ?? relation,
    };
    return onSave(data);
  };
  isDateRangeInvalid = () => {
    const { dateFrom, dateUntil } = this.state;
    if (!dateFrom || !dateUntil) return true;
    if (!moment.utc(dateFrom).isValid()) return true;
    if (!moment.utc(dateUntil).isValid()) return true;
    if (moment.utc(dateUntil).isBefore(moment.utc(dateFrom))) return true;
    if (moment.utc(dateUntil).diff(moment.utc(dateFrom), 'years', true) > 1) return true;
    return false;
  };
  isFormInvalid = () => {
    const { resource, dateFrom, dateUntil } = this.state;
    if (!resource?.id) return true;
    if (this.isDateRangeInvalid()) return true;

    return false;
  };

  showValidation = () => this.setState({ showValidation: true });

  datesChanged = ({ startDate, endDate }) => {
    this.setState({ dateFrom: startDate, dateUntil: endDate });
  };
  onResourceChanged = resource => {
    this.setState({ resource });
  };
  onRelationChanged = rel => {
    this.setState({ relation });
  };
  render() {
    const { show, intl, onClose, resourceType } = this.props;
    const {
      resource,
      resources,
      showValidation,
      dateFrom,
      dateUntil,
      relationId,
      ownCompanyRelations,
    } = this.state;
    const crmTypes = resourceType === 3 || resourceType === 4 || resourceType === 5 ? [4] : [3, 4];
    const invalidDate = this.isDateRangeInvalid();
    return (
      <OkCancelModal
        title={intl.formatMessage(messages.BatchFillPeriod)}
        show={show}
        onClose={onClose}
        disabled={showValidation && this.isFormInvalid()}
        onSave={this.saveRegistration}
        onHoverSave={this.showValidation}
      >
        <div className="form-horizontal">
          <div className="form-group">
            <FormGroupPart
              label={messages.RegTimeFromTo}
              inputCols="col-sm-9"
              labelCols="col-sm-3"
              invalid={showValidation && invalidDate}
            >
              <DelayedDateRangePicker
                startDate={dateFrom}
                endDate={dateUntil}
                onDatesChange={this.datesChanged}
                block
              />
            </FormGroupPart>
          </div>
          {ownCompanyRelations && ownCompanyRelations.length > 1 ? (
            <div className="form-group">
              <FormGroupPart label={messages.Relation} inputCols="col-sm-9" labelCols="col-sm-3">
                <select
                  className="form-control"
                  value={relationId || ''}
                  onChange={e => this.setState({ relationId: e.target.value || null })}
                >
                  <option>
                    {intl.formatMessage(messages.UseDefaultAbsenceRelationOfEmployee)}
                  </option>
                  {ownCompanyRelations.map(rel => (
                    <option value={rel.id} key={rel.id}>
                      {rel.label}
                    </option>
                  ))}
                </select>
              </FormGroupPart>
            </div>
          ) : null}
          <div className="form-group">
            <FormGroupPart
              label={messages.What}
              inputCols="col-sm-9"
              labelCols="col-sm-3"
              invalid={showValidation && !resource?.id}
            >
              {resources && resources.length > 0 && (
                <ResourceAutoComplete
                  items={resources}
                  placeholder={intl.formatMessage(messages.SearchPriceListItem)}
                  onChange={this.onResourceChanged}
                  emptyLabel={intl.formatMessage(messages.NoPriceListItemsFound)}
                  value={resource}
                  required
                  crmType={4}
                  intl={intl}
                  isSmall
                />
              )}
            </FormGroupPart>
          </div>
        </div>
      </OkCancelModal>
    );
  }
}

BatchFillGapsRegistrationModal.propTypes = {
  resourceType: PropTypes.number.isRequired,
  onSave: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  onClose: PropTypes.func.isRequired,
  show: PropTypes.bool,
  dateFrom: momentPropTypes.momentObj,
  dateUntil: momentPropTypes.momentObj,
};

BatchFillGapsRegistrationModal.defaultProps = {
  show: false,
  dateFrom: moment.utc().startOf('day'),
  dateUntil: moment.utc().startOf('day'),
};
export default injectIntl(BatchFillGapsRegistrationModal);
